import { useQueries } from '@tanstack/react-query';
import { Expenditure } from 'src/cashflows/hooks/useExpenditures';
import { Income } from 'src/cashflows/hooks/useIncomes';
import { LifeEvent } from 'src/cashflows/hooks/useLifeEvents';
import { FamilyMember } from './useFamilyMembers';
import {
  aggregateFamilyExpenditures,
  aggregateFamilyIncomes,
  aggregateFamilyLifeEvents,
  FamilyExpenditure,
  FamilyIncome,
  FamilyLifeEvent,
} from './familyCashflowsAggregation';

export function useFamilyCashflows(members: FamilyMember[]): {
  incomes: FamilyIncome[];
  expenditures: FamilyExpenditure[];
  lifeEvents: FamilyLifeEvent[];
  isLoading: boolean;
  hasErrors: boolean;
} {
  const incomeResults = useQueries({
    queries: members.map(member => ({
      queryKey: ['clients', member.clientId, 'incomes'],
      enabled: !!member.clientId,
    })),
  });
  const expenditureResults = useQueries({
    queries: members.map(member => ({
      queryKey: ['clients', member.clientId, 'expenditures'],
      enabled: !!member.clientId,
    })),
  });
  const lifeEventResults = useQueries({
    queries: members.map(member => ({
      queryKey: ['clients', member.clientId, 'life-events'],
      enabled: !!member.clientId,
    })),
  });

  const allResults = [...incomeResults, ...expenditureResults, ...lifeEventResults];
  const isLoading = allResults.some(result => result.isLoading);
  const hasErrors = allResults.some(result => result.isError);

  const incomes = aggregateFamilyIncomes(
    members,
    incomeResults.map(result => result.data as Income[] | undefined),
  );
  const expenditures = aggregateFamilyExpenditures(
    members,
    expenditureResults.map(result => result.data as Expenditure[] | undefined),
  );
  const lifeEvents = aggregateFamilyLifeEvents(
    members,
    lifeEventResults.map(result => result.data as LifeEvent[] | undefined),
  );

  return { incomes, expenditures, lifeEvents, isLoading, hasErrors };
}
